import { getCSRFHeaders, postJSON } from '@folklore/fetch';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import useSetUser from '../../hooks/useSetUser';
import * as AppPropTypes from '../../lib/PropTypes';

import { useLabels } from '../../contexts/LabelsContext';
import { useThemeClassName } from '../../contexts/ThemeContext';
import StyledButton from '../buttons/StyledButton';

import styles from '../../../styles/forms/redeem-coupon-form.module.scss';

const propTypes = {
    className: PropTypes.string,
    prize: AppPropTypes.prize,
    onClickClose: PropTypes.func,
    onSuccess: PropTypes.func,
};

const defaultProps = {
    className: null,
    prize: null,
    onClickClose: null,
    onSuccess: null,
};

function RedeemCouponForm({ className, prize, onClickClose, onSuccess }) {
    const {
        redeemCouponTitle,
        redeemCouponDescription,
        redeemCouponCancelButton,
        redeemCouponActivateButton,
    } = useLabels();
    const route = useUrlGenerator();
    const themeClassName = useThemeClassName(styles);
    const { claimExpiration = 10 } = prize;

    const [loading, setLoading] = useState(false);
    const setUser = useSetUser();

    const onCancelClick = useCallback(() => {
        if (onClickClose !== null) {
            onClickClose();
        }
    }, [onClickClose]);

    const onConfirmClick = useCallback(() => {
        const { uuid } = prize || {};
        setLoading(true);
        postJSON(
            route('prize.claim', { participant_prize: uuid }),
            {},
            {
                credentials: 'include',
                headers: getCSRFHeaders(),
            },
        )
            .then((user) => {
                setUser(user);
                if (onSuccess !== null) {
                    onSuccess(user);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, [setUser, prize, onClickClose]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.loading]: loading,
                },
                themeClassName,
                className,
            ])}
        >
            {redeemCouponTitle !== false ? (
                <h1 className={styles.title}>
                    {redeemCouponTitle ?? (
                        <FormattedMessage
                            defaultMessage="Activate the Voucher"
                            description="RedeemCouponConfirm title"
                        />
                    )}
                </h1>
            ) : null}
            {redeemCouponDescription !== false ? (
                <p className={styles.description}>
                    {redeemCouponDescription || (
                        <FormattedMessage
                            defaultMessage="Make sure you are near a checkout before activating the voucher.{br}{br}You will have {minutes} minutes to redeem it."
                            description="RedeemCouponConfirm description"
                            values={{ minutes: claimExpiration || 10 }}
                        />
                    )}
                </p>
            ) : null}
            <div className={styles.actions}>
                <StyledButton bordered className={styles.cancel} onClick={onCancelClick} disabled={loading}>
                    {redeemCouponCancelButton || (
                        <FormattedMessage
                            defaultMessage="Cancel"
                            description="RedeemCouponConfirm cancel label"
                        />
                    )}
                </StyledButton>
                <StyledButton
                    colored
                    className={styles.submit}
                    onClick={onConfirmClick}
                    disabled={loading}
                >
                    {redeemCouponActivateButton || (
                        <FormattedMessage
                            defaultMessage="Activate"
                            description="RedeemCouponConfirm submit label"
                        />
                    )}
                </StyledButton>
            </div>
        </div>
    );
}

RedeemCouponForm.propTypes = propTypes;
RedeemCouponForm.defaultProps = defaultProps;

export default RedeemCouponForm;
